import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { useAppStore } from '../../store/useAppStore'
import ClearableBadge from './ClearableBadge'
import './PermissionsFilterDropdown.scss'

export interface User {
  id: number
  username: string
  first_name?: string
  last_name?: string
}

export enum OwnerFilterType {
  NONE = 'none',
  SELF = 'self',
  NOT_SELF = 'not_self',
  OTHERS = 'others',
  UNOWNED = 'unowned',
  SHARED_BY_ME = 'shared_by_me',
}

export interface PermissionsSelectionModel {
  ownerFilter: OwnerFilterType
  hideUnowned: boolean
  userID: number | null
  includeUsers: number[]
  excludeUsers: number[]
}

interface PermissionsFilterDropdownProps {
  title: string
  icon: string
  selectionModel: PermissionsSelectionModel
  onSelectionModelChange: (model: PermissionsSelectionModel) => void
  users?: User[]
  disabled?: boolean
  className?: string
  fullWidth?: boolean
}

export const PermissionsFilterDropdown: React.FC<PermissionsFilterDropdownProps> = ({
  title,
  icon,
  selectionModel,
  onSelectionModelChange,
  users = [],
  disabled = false,
  className = '',
  fullWidth = true,
}) => {
  const { t } = useTranslation()
  const { user: currentUser } = useAppStore()
  const [show, setShow] = useState(false)
  const getInitialDropDirection = () => {
    if (typeof window === 'undefined') return 'down'
    return window.innerWidth >= 992 ? 'end' : 'down'
  }
  const [dropDirection, setDropDirection] = useState<'down' | 'end'>(getInitialDropDirection)
  const [overlayContainer, setOverlayContainer] = useState<HTMLElement | null>(null)
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 360 })

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setOverlayContainer(document.body)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(min-width: 992px)')
    const updateDirection = () => setDropDirection(mediaQuery.matches ? 'end' : 'down')
    updateDirection()
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateDirection)
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(updateDirection)
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateDirection)
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(updateDirection)
      }
    }
  }, [])

  const popperConfig = useMemo(
    () => ({
      strategy: 'fixed' as const,
      modifiers: [
        { name: 'applyStyles', enabled: false },
        { name: 'eventListeners', enabled: false },
      ],
    }),
    []
  )

  const updateMenuPosition = useCallback(() => {
    if (typeof window === 'undefined') return
    const buttonRect = toggleRef.current?.getBoundingClientRect()
    if (!buttonRect) return
    const panel = document.querySelector('.documents-filters-panel') as HTMLElement | null
    const panelRect = panel?.getBoundingClientRect()
    const spacing = 16
    const viewportWidth = window.innerWidth
    const isDesktop = viewportWidth >= 992

    let width = Math.min(420, viewportWidth - spacing * 2)
    let left = isDesktop && panelRect ? panelRect.right + spacing : spacing

    if (left + width > viewportWidth - spacing) {
      left = viewportWidth - width - spacing
    }
    if (left < spacing) {
      left = spacing
    }

    const top = buttonRect.top + window.scrollY

    setMenuPosition({
      top,
      left: left + window.scrollX,
      width,
    })
  }, [])

  useEffect(() => {
    if (!show) return
    updateMenuPosition()
    const handleUpdate = () => updateMenuPosition()
    window.addEventListener('resize', handleUpdate)
    window.addEventListener('scroll', handleUpdate, true)
    return () => {
      window.removeEventListener('resize', handleUpdate)
      window.removeEventListener('scroll', handleUpdate, true)
    }
  }, [show, updateMenuPosition])

  const isActive = (): boolean => {
    return selectionModel.ownerFilter !== OwnerFilterType.NONE || selectionModel.hideUnowned
  }

  const getActiveCount = (): number => {
    let count = 0
    if (selectionModel.ownerFilter !== OwnerFilterType.NONE) count++
    if (selectionModel.hideUnowned) count++
    if (selectionModel.includeUsers.length > 0) count += selectionModel.includeUsers.length
    return count
  }

  const getButtonVariant = (): string => {
    return isActive() ? 'primary' : 'outline-primary'
  }

  const setFilter = (type: OwnerFilterType) => {
    const newModel = { ...selectionModel }
    newModel.ownerFilter = type

    // Clear selections based on filter type
    if (type === OwnerFilterType.SELF || type === OwnerFilterType.SHARED_BY_ME) {
      newModel.userID = currentUser?.id || null
      newModel.includeUsers = []
      newModel.excludeUsers = []
      newModel.hideUnowned = false
    } else if (type === OwnerFilterType.NOT_SELF) {
      newModel.userID = null
      newModel.includeUsers = []
      newModel.excludeUsers = currentUser?.id ? [currentUser.id] : []
      newModel.hideUnowned = false
    } else if (type === OwnerFilterType.NONE) {
      newModel.userID = null
      newModel.includeUsers = []
      newModel.excludeUsers = []
      newModel.hideUnowned = false
    } else if (type === OwnerFilterType.UNOWNED) {
      newModel.userID = null
      newModel.includeUsers = []
      newModel.excludeUsers = []
      newModel.hideUnowned = false
    }

    onSelectionModelChange(newModel)
  }

  const onUserSelect = (selectedIds: number[]) => {
    const newModel = { ...selectionModel }
    newModel.includeUsers = selectedIds

    if (selectedIds.length > 0) {
      newModel.ownerFilter = OwnerFilterType.OTHERS
    } else {
      newModel.ownerFilter = OwnerFilterType.NONE
    }

    onSelectionModelChange(newModel)
  }

  const reset = () => {
    const newModel: PermissionsSelectionModel = {
      ownerFilter: OwnerFilterType.NONE,
      hideUnowned: false,
      userID: null,
      includeUsers: [],
      excludeUsers: [],
    }
    onSelectionModelChange(newModel)
  }

  const handleHideUnownedChange = () => {
    const newModel = { ...selectionModel, hideUnowned: !selectionModel.hideUnowned }
    onSelectionModelChange(newModel)
  }

  const userOptions = users.map((user) => ({
    value: user.id,
    label: user.username,
  }))

  const selectedUserOptions = userOptions.filter((opt) => selectionModel.includeUsers.includes(opt.value))

  return (
    <Dropdown
      show={show}
      onToggle={(nextShow) => {
        if (nextShow) {
          updateMenuPosition()
        }
        setShow(nextShow)
      }}
      className={[className, fullWidth ? 'w-100' : ''].filter(Boolean).join(' ')}
      drop={dropDirection}
      autoClose="outside"
    >
      <Dropdown.Toggle
        variant={getButtonVariant()}
        size="sm"
        disabled={disabled}
        className={`${fullWidth ? 'w-100 ' : ''}d-flex align-items-center justify-content-between text-start`}
        style={{ whiteSpace: 'nowrap' }}
        ref={toggleRef}
      >
        <span className="filter-chip__label">
          <i className={`bi bi-${icon}`}></i>
          <span>{title}</span>
        </span>
        {getActiveCount() > 0 && <ClearableBadge number={getActiveCount()} selected={isActive()} onCleared={reset} />}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="shadow permission-filter-dropdown py-0 filter-dropdown-menu"
        ref={menuRef}
        style={{
          position: 'fixed',
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: `${menuPosition.width}px`,
          minWidth: `${menuPosition.width}px`,
          transform: 'none',
        }}
        popperConfig={popperConfig}
      >
        <div className="list-group list-group-flush">
          {/* All */}
          <button
            className="list-group-item list-group-item-action d-flex align-items-center p-2 border-top-0 border-start-0 border-end-0 border-bottom"
            role="menuitem"
            onClick={() => setFilter(OwnerFilterType.NONE)}
            disabled={disabled}
          >
            <div className="selected-icon me-1" style={{ width: '20px' }}>
              {selectionModel.ownerFilter === OwnerFilterType.NONE && <i className="bi bi-check"></i>}
            </div>
            <div>
              <small>{t('all')}</small>
            </div>
          </button>

          {/* My documents */}
          <button
            className="list-group-item list-group-item-action d-flex align-items-center p-2 border-top-0 border-start-0 border-end-0 border-bottom"
            role="menuitem"
            onClick={() => setFilter(OwnerFilterType.SELF)}
            disabled={disabled}
          >
            <div className="selected-icon me-1" style={{ width: '20px' }}>
              {selectionModel.ownerFilter === OwnerFilterType.SELF && <i className="bi bi-check"></i>}
            </div>
            <div>
              <small>{t('my_documents')}</small>
            </div>
          </button>

          {/* Shared with me */}
          <button
            className="list-group-item list-group-item-action d-flex align-items-center p-2 border-top-0 border-start-0 border-end-0 border-bottom"
            role="menuitem"
            onClick={() => setFilter(OwnerFilterType.NOT_SELF)}
            disabled={disabled}
          >
            <div className="selected-icon me-1" style={{ width: '20px' }}>
              {selectionModel.ownerFilter === OwnerFilterType.NOT_SELF && <i className="bi bi-check"></i>}
            </div>
            <div>
              <small>{t('shared_with_me')}</small>
            </div>
          </button>

          {/* Shared by me */}
          <button
            className="list-group-item list-group-item-action d-flex align-items-center p-2 border-top-0 border-start-0 border-end-0 border-bottom"
            role="menuitem"
            onClick={() => setFilter(OwnerFilterType.SHARED_BY_ME)}
            disabled={disabled}
          >
            <div className="selected-icon me-1" style={{ width: '20px' }}>
              {selectionModel.ownerFilter === OwnerFilterType.SHARED_BY_ME && <i className="bi bi-check"></i>}
            </div>
            <div>
              <small>{t('shared_by_me')}</small>
            </div>
          </button>

          {/* Unowned */}
          <button
            className="list-group-item list-group-item-action d-flex align-items-center p-2 border-top-0 border-start-0 border-end-0 border-bottom"
            role="menuitem"
            onClick={() => setFilter(OwnerFilterType.UNOWNED)}
            disabled={disabled}
          >
            <div className="selected-icon me-1" style={{ width: '20px' }}>
              {selectionModel.ownerFilter === OwnerFilterType.UNOWNED && <i className="bi bi-check"></i>}
            </div>
            <div>
              <small>{t('unowned')}</small>
            </div>
          </button>

          {/* Users Select */}
          {users.length > 0 && (
            <div className="list-group-item list-group-item-action d-flex align-items-center p-2 border-top-0 border-start-0 border-end-0 border-bottom">
              <div className="selected-icon me-1" style={{ width: '20px' }}>
                {selectionModel.ownerFilter === OwnerFilterType.OTHERS && <i className="bi bi-check"></i>}
              </div>
              <div className="flex-grow-1">
                <Select
                  isMulti
                  options={userOptions}
                  value={selectedUserOptions}
                  onChange={(selected) => {
                    const selectedIds = selected ? selected.map((opt) => opt.value) : []
                    onUserSelect(selectedIds)
                  }}
                  isDisabled={disabled}
                  placeholder={t('users')}
                  className="user-select"
                  classNamePrefix="select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '32px',
                      fontSize: '0.875rem',
                    }),
                    menu: (base) => ({
                      ...base,
                      fontSize: '0.875rem',
                    }),
                  }}
                />
              </div>
            </div>
          )}

          {/* Hide unowned checkbox */}
          {(selectionModel.ownerFilter === OwnerFilterType.NONE || selectionModel.ownerFilter === OwnerFilterType.NOT_SELF) && (
            <div className="list-group-item list-group-item-action d-flex align-items-center p-2 ps-3 border-bottom-0 border-start-0 border-end-0">
              <div className="form-check form-switch w-100">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="hideUnowned"
                  checked={selectionModel.hideUnowned}
                  onChange={handleHideUnownedChange}
                  disabled={disabled}
                />
                <label className="form-check-label w-100" htmlFor="hideUnowned">
                  <small>{t('hide_unowned')}</small>
                </label>
              </div>
            </div>
          )}
        </div>
      </Dropdown.Menu>
      {show && overlayContainer && createPortal(<div className="filter-panel-overlay" onClick={() => setShow(false)}></div>, overlayContainer)}
    </Dropdown>
  )
}

export default PermissionsFilterDropdown
